import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QuietButton } from "@/components/brand/QuietButton";
import { BrandCard } from "@/components/brand/BrandCard";
import { Chip } from "@/components/brand/Chip";
import { GooglePlayBadge } from "@/components/brand/GooglePlayBadge";
import { RevealText, Reveal } from "@/components/brand/Reveal";
import { ScreenshotFrame } from "@/components/brand/ScreenshotFrame";
import { SEO } from "@/components/SEO";
import { getPostBySlug } from "@/content/blogPosts";
import { SITE_NAME, SITE_URL, SUPPORT_EMAIL, PUBLISHER, GOOGLE_PLAY_URL } from "@/content/siteConfig";

const homeBlogPool = [
  "block-apps-child-uses-my-phone",
  "who-crenelview-is-built-for",
  "why-your-child-cries-when-you-say-no-to-the-phone",
  "what-to-check-before-handing-your-child-your-phone",
  "screen-pinning-vs-app-blocker-shared-phone",
  "how-to-tell-parental-control-app-from-spyware",
  "parental-control-no-phone-of-their-own",
  "relative-hands-child-their-phone",
];

function getRotatedBlogSlugs(pool: string[], count: number) {
  const now = new Date();
  const yearStart = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
  const week = Math.floor((now.getTime() - yearStart.getTime()) / (7 * 24 * 60 * 60 * 1000));
  const startIndex = week % pool.length;
  return Array.from({ length: count }, (_, i) => pool[(startIndex + i) % pool.length]);
}

const steps: {
  title: string;
  description: string;
  screenshot?: string;
  alt?: string;
  screenshotLabel?: string;
}[] = [
  {
    title: "Install CrenelView from the Google Play Store.",
    description: "One app, on your own phone. No child device and no child account are needed.",
    screenshot: "/screenshots/welcome-setup.webp",
    alt: "CrenelView welcome screen reading Your phone looks normal, the apps you choose simply do not open, with a Start setup button",
  },
  {
    title: "Open the app and set your parent PIN.",
    description: "The PIN protects your settings, with a recovery code and a 24 hour reset if you lose it.",
    screenshot: "/screenshots/set-pin.webp",
    alt: "CrenelView Set your parent PIN screen with PIN and Confirm PIN fields",
  },
  {
    title: "Turn on the accessibility service in Settings.",
    description: "This is the one manual step, and the longest part of setup. CrenelView takes you to your phone's Settings, you switch its accessibility service on by hand, then come back to the app. It is required for blocking to work at all.",
    screenshot: "/screenshots/accessibility-service.webp",
    alt: "Android accessibility settings screen for CrenelView protection, with the CrenelView protection toggle switched on",
  },
  {
    title: "Tick the apps that must not open.",
    description: "Pick from the apps already installed on your phone. That is the whole setup from here.",
    screenshot: "/screenshots/choose-apps.webp",
    alt: "CrenelView Blocked apps screen listing installed apps with checkboxes, Roblox ticked",
  },
  {
    title: "Choose one mode for your whole list: always, or only certain hours.",
    description: "This is a single setting for the entire block list, not per app. Either every app on it stays blocked all the time, or every app on it is blocked only during hours you choose, for example 20:00 to 07:00.",
    screenshot: "/screenshots/schedule-hours.webp",
    alt: "CrenelView What to block screen with At set times selected and blocked hours 20:00 to 07:00",
  },
  {
    title: "Hand the phone over when needed.",
    description: "Tap 10, 15, 30 or 60 minutes to unblock your whole list for exactly that long. Everything on it opens again until the timer runs out, then it all blocks itself again on its own.",
    screenshot: "/screenshots/hand-phone-over.webp",
    alt: "CrenelView Blocked apps screen with Allow for a while buttons for 10, 15, 30 and 60 minutes",
  },
  {
    title: "New games join the list on their own.",
    description: "Any app your phone's own system categorizes as a game is added to your block list automatically when it is installed later. This applies to games only, decided by that category, not to other kinds of apps.",
    screenshot: "/screenshots/new-apps-auto.webp",
    alt: "CrenelView Block new games automatically toggle switched on, with the Is it working section below it",
  },
  {
    title: "Run a self check whenever you want.",
    description: "It tells you whether protection is actually active on your particular phone, right now.",
    screenshot: "/screenshots/self-check-result.webp",
    alt: "CrenelView self check result screen reading Everything is working, with five OK checks listed",
  },
];

export default function Home() {
  // Initial render matches the prerendered static HTML exactly (fixed order) so hydration never
  // mismatches; the weekly rotation is applied client-side only, after mount.
  const [homeBlogSlugs, setHomeBlogSlugs] = useState(() => homeBlogPool.slice(0, 4));
  useEffect(() => {
    setHomeBlogSlugs(getRotatedBlogSlugs(homeBlogPool, 4));
  }, []);
  return (
    <>
      <SEO
        title={`${SITE_NAME} - block apps on the phone your child uses`}
        description="CrenelView blocks the apps you choose on your own phone, so a young child using it cannot open them. No account, no server, no internet permission."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: SITE_NAME,
            operatingSystem: "Android",
            applicationCategory: "ParentingApplication",
            url: SITE_URL,
            installUrl: GOOGLE_PLAY_URL,
            publisher: { "@type": "Organization", name: PUBLISHER },
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
            logo: `${SITE_URL}/brand/play-icon-512.png`,
            email: SUPPORT_EMAIL,
          },
        ]}
      />

      <section className="mx-auto max-w-6xl px-6 pt-14 pb-16 sm:pt-20 sm:pb-24">
        <RevealText
          lines={["Blocks the apps you choose.", "So your child cannot open them."]}
          className="font-heading text-4xl leading-[1.15] sm:text-5xl lg:text-6xl"
          as="h1"
        />
        <Reveal delay={0.05} className="mt-6 max-w-lg text-base text-cvbody sm:text-lg">
          CrenelView runs on your own phone. There is no child account, no second device.
          One trip into Settings during setup, then nothing after.
        </Reveal>
        <Reveal delay={0.1} className="mt-8 flex flex-wrap items-center gap-4">
          <GooglePlayBadge data-testid="hero-play-cta" className="h-14" />
          <QuietButton to="/#how-it-works" data-testid="hero-how-it-works-link" className="h-14 items-center justify-center">
            See how it works
          </QuietButton>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-10 sm:py-14">
        <Reveal>
          <p className="max-w-2xl font-heading text-2xl sm:text-3xl">
            Your child uses your phone. There is no child account to manage.
          </p>
          <p className="mt-5 max-w-2xl text-base text-cvbody sm:text-lg">
            Most parental control apps expect a phone that belongs to your child, with its own
            account. If your child does not have a phone of their own and simply uses yours, that
            setup does not apply. CrenelView works directly on your phone instead. Tick an app,
            and it closes itself and returns to the home screen whenever it is opened while
            blocked.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Chip>YouTube</Chip>
            <Chip>Games</Chip>
            <Chip>Camera</Chip>
            <Chip>Browser</Chip>
          </div>
        </Reveal>
      </section>

      <section id="how-it-works" className="mx-auto max-w-6xl px-6 py-10 sm:py-14">
        <Reveal>
          <h2 className="font-heading text-3xl sm:text-4xl">How CrenelView works.</h2>
          <p className="mt-4 max-w-xl text-base text-cvbody sm:text-lg">
            Eight steps, start to finish. Every step happens on your own phone. Nothing leaves it.
          </p>
        </Reveal>

        <div className="mt-4">
          {steps.map((step, i) => (
            <Reveal key={step.title} className="grid grid-cols-1 gap-8 border-t border-cvborder py-10 sm:grid-cols-[auto,1fr,auto] sm:items-center sm:gap-10 sm:py-12">
              <span className="font-serif text-4xl text-periwinkle sm:text-5xl">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="font-heading text-2xl sm:text-3xl">{step.title}</h3>
                <p className="mt-3 max-w-md text-base text-cvbody">{step.description}</p>
              </div>
              <ScreenshotFrame
                src={step.screenshot}
                alt={step.alt || step.title}
                label={step.screenshotLabel}
                className="max-w-[160px] justify-self-start sm:justify-self-end"
              />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 sm:py-24">
        <Reveal>
          <BrandCard className="flex flex-col items-start gap-6 p-8 sm:flex-row sm:items-center sm:justify-between sm:p-12">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl">Set it up once, then hand the phone over.</h2>
              <p className="mt-2 max-w-md text-base text-cvbody">Nothing else to manage, and nothing to hand over but the phone.</p>
            </div>
            <GooglePlayBadge data-testid="home-bottom-play-cta" className="h-14" />
          </BrandCard>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-10 sm:py-14" data-testid="home-blog-section">
        <Reveal>
          <h2 className="font-heading text-3xl sm:text-4xl">From the blog.</h2>
          <p className="mt-4 max-w-xl text-base text-cvbody sm:text-lg">
            Plain, practical writing on shared-phone parenting.
          </p>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {homeBlogSlugs.map((slug, i) => {
            const post = getPostBySlug(slug);
            if (!post) return null;
            return (
              <Reveal key={slug} delay={Math.min(i * 0.05, 0.15)}>
                <Link to={`/blog/${slug}`} data-testid={`home-blog-card-${slug}`}>
                  <BrandCard className="h-full transition-transform duration-200 ease-out hover:-translate-y-0.5">
                    <h3 className="font-heading text-xl sm:text-2xl">{post.title}</h3>
                    <p className="mt-3 text-base text-cvbody">{post.description}</p>
                    <span className="mt-4 inline-block text-sm font-semibold text-cvlink">Read more -&gt;</span>
                  </BrandCard>
                </Link>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} className="mt-8">
          <Link to="/blog" className="text-sm font-semibold text-cvlink" data-testid="home-blog-see-all-link">
            See all posts -&gt;
          </Link>
        </Reveal>
      </section>
    </>
  );
}
